"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAvailablePaymentFrequencies = getAvailablePaymentFrequencies;
exports.getNearestPaymentFrequency = getNearestPaymentFrequency;
exports.getFactorRate = getFactorRate;
exports.getParamBoundaries = getParamBoundaries;
exports.getOriginationParams = getOriginationParams;
exports.getOptionAvailableAmount = getOptionAvailableAmount;
exports.getPaybackAmount = getPaybackAmount;
exports.getCommission = getCommission;
exports.getAchPaymentAmount = getAchPaymentAmount;
const lodash_1 = require("lodash");
const rounding_1 = require("../rounding");
const payment_frequencies_1 = require("./types/payment-frequencies");
function getAvailablePaymentFrequencies(options, term) {
    return options
        .filter(option => option.term === term)
        .reduce((frequencies, option) => {
        if (!frequencies.includes(option.paymentFrequency)) {
            const optionFrequency = payment_frequencies_1.FREQ_MAP[option.paymentFrequency];
            return optionFrequency
                ? [
                    ...frequencies,
                    optionFrequency.value,
                ]
                : frequencies;
        }
        return frequencies;
    }, []);
}
function getNearestPaymentFrequency(paymentFrequency, possibleFrequencies = []) {
    if (possibleFrequencies.includes(paymentFrequency)) {
        return paymentFrequency;
    }
    const goalFrequency = payment_frequencies_1.FREQ_MAP[paymentFrequency], goalFrequencyDays = goalFrequency.numDays;
    return possibleFrequencies.sort((a, b) => {
        const aDelta = Math.abs(payment_frequencies_1.FREQ_MAP[a].numDays - goalFrequencyDays), bDelta = Math.abs(payment_frequencies_1.FREQ_MAP[b].numDays - goalFrequencyDays);
        return aDelta - bDelta;
    })[0];
}
function getFactorRate(pointsTradable, buyRate, points, maxOriginationPercent, originationPercent) {
    let factor = buyRate + (points / 100);
    if (pointsTradable) {
        factor += ((maxOriginationPercent - originationPercent) / 100);
    }
    return (0, rounding_1.toFixedNumber)(factor, 3);
}
/**
 * Reduces an array of approval options down to a min and max boundary for a single parameter
 * Works for a parameter of the option, or an option's limits IE: option[param] || option.limits[param]
 * @param options
 * @param param
 */
function getParamBoundaries(options, param) {
    return options.reduce((res, option) => {
        const optionParam = (0, lodash_1.get)(option, param) || (0, lodash_1.get)(option.limits, param);
        const optionMinValue = typeof optionParam === 'object' ?
            optionParam.min :
            optionParam;
        const optionMaxValue = typeof optionParam === 'object' ?
            optionParam.max :
            optionParam;
        return {
            min: Math.min(optionMinValue, res.min),
            max: Math.max(optionMaxValue, res.max)
        };
    }, {
        min: 9999999999999,
        max: 0
    });
}
function getOriginationParams(optionLimits, amount) {
    // define min & max - explicit amounts take priority over percentages
    const optionOriginAmountLimits = optionLimits.originationAmount;
    const explicitMax = optionOriginAmountLimits.max;
    let explicitMin = optionOriginAmountLimits.min;
    const maxOriginationPercent = optionLimits.originationPercent.max;
    const minOriginationPercent = optionLimits.originationPercent.min;
    const calculatedMaxPercent = explicitMax ?
        (explicitMax / amount) * 100 :
        maxOriginationPercent;
    const calculatedMinPercent = explicitMin ?
        (explicitMin / amount) * 100 :
        minOriginationPercent;
    const originationLimitsAreEqual = minOriginationPercent === maxOriginationPercent;
    const minThreshhold = explicitMin ?
        explicitMin / (minOriginationPercent / 100) :
        0;
    const maxThreshhold = explicitMax ?
        explicitMax / (maxOriginationPercent / 100) :
        0;
    let absMaxOriginationPercent, absMinOriginationPercent;
    /**
     * Has a specific $ origination amount MINIMUM but not MAXIMUM
     */
    if (explicitMin && !explicitMax) {
        absMinOriginationPercent = Math.max(minOriginationPercent, calculatedMinPercent);
        absMaxOriginationPercent = Math.max(absMinOriginationPercent, maxOriginationPercent);
        explicitMin = (0, rounding_1.toFixedNumber)(amount * (absMinOriginationPercent / 100), 2, 10);
    }
    /**
     * Has a specific $ origination amount MAXIMUM but not MINIMUM
     */
    if (!explicitMin && explicitMax) {
        absMaxOriginationPercent = Math.min(maxOriginationPercent, calculatedMaxPercent);
        absMinOriginationPercent = Math.min(absMaxOriginationPercent, minOriginationPercent);
    }
    /**
     * Has a specific $ origination amount MAXIMUM and MINIMUM
     */
    if (explicitMin && explicitMax) {
        absMinOriginationPercent = Math.max(calculatedMinPercent, minOriginationPercent);
        const ceiling = Math.min(calculatedMaxPercent, maxOriginationPercent);
        absMaxOriginationPercent = Math.max(ceiling, absMinOriginationPercent);
    }
    /**
     * Has NO specific $ origination amount
     */
    if (!explicitMin && !explicitMax) {
        absMinOriginationPercent = minOriginationPercent;
        absMaxOriginationPercent = maxOriginationPercent;
    }
    const minOriginationAmount = (0, rounding_1.toFixedNumber)(amount * (absMinOriginationPercent / 100), 2, 10);
    const maxOriginationAmount = (0, rounding_1.toFixedNumber)(amount * (absMaxOriginationPercent / 100), 2, 10);
    const maxBoundary = explicitMax || maxOriginationAmount;
    const absMinOriginationAmount = originationLimitsAreEqual ?
        Math.min(minOriginationAmount, maxBoundary) :
        explicitMin || minOriginationAmount;
    const absMaxOriginationAmount = originationLimitsAreEqual ?
        Math.min(maxOriginationAmount, maxBoundary) :
        explicitMax || maxOriginationAmount;
    absMinOriginationPercent = (0, rounding_1.toFixedNumber)(absMinOriginationPercent, 2, 10);
    absMaxOriginationPercent = (0, rounding_1.toFixedNumber)(absMaxOriginationPercent, 2, 10);
    return {
        originationAmount: {
            min: minOriginationAmount,
            absMin: absMinOriginationAmount,
            max: maxOriginationAmount,
            absMax: absMaxOriginationAmount
        },
        originationPercent: {
            min: minOriginationPercent,
            absMin: absMinOriginationPercent,
            max: maxOriginationPercent,
            absMax: absMaxOriginationPercent
        },
        originationThreshhold: {
            min: minThreshhold,
            max: maxThreshhold,
        },
        originationLimitsAreEqual
    };
}
function getOptionAvailableAmount(option, calculationType, factor, roundingStrategy) {
    switch (calculationType) {
        case 'standard':
            const achOption = option;
            return achOption.limits.amount.max;
        case 'maxPayment':
            const mpAchOption = option;
            const base = mpAchOption.limits.paymentAmountMax * mpAchOption.payments / factor;
            const calcNum = (0, rounding_1.getValueByRoundingStrategy)(roundingStrategy, base);
            return Math.min(mpAchOption.limits.amount.max, calcNum);
        case 'loc':
            const locOption = option;
            return locOption.limits.drawAmount.max;
        default:
            const defaultOption = option;
            return defaultOption.limits.amount.max;
    }
}
/**
 *
 * @param factor
 * @param amount
 * @returns {number}
 */
function getPaybackAmount(factor, amount) {
    return (0, rounding_1.toFixedNumber)(factor * amount, 3);
}
/**
 *
 * @param amount
 * @param points
 * @returns {number}
 */
function getCommission(amount, points) {
    const base = amount * (points / 100);
    return (0, rounding_1.toFixedNumber)(base, 3);
}
function getAchPaymentAmount(paybackAmount, numPayments) {
    const base = (paybackAmount * 10000) / (numPayments * 10000);
    return (0, rounding_1.toFixedNumber)(base, 2);
}
